import { formatEmissionUnit } from "@/lib/emissionUnits";
import type { ChartSpec } from "../../../types";
import { getChartFontFamily, readThemeColors } from "../theme";
import { conversionFactor, convertWithUnit, formatLargeNumber } from "../units";

export function createChartContext(
  spec: ChartSpec,
  isDark: boolean,
  width: number,
  translateNode: (text: string) => string,
) {
  const { chartType, title, data } = spec;
  const rawUnit = data.unit;

  const allValues = (data.datasets || []).flatMap(
    (ds) => ds.values as unknown[],
  );
  const linkValues = (data.links || []).map((l) => l.value);
  const combined = [...allValues, ...linkValues];
  const maxVal = combined.reduce(
    (m: number, v: unknown) =>
      Math.max(m, Math.abs(typeof v === "number" ? v : 0)),
    0,
  );
  const respectUnit = data.respectUnit === true;
  const converted = convertWithUnit(maxVal, rawUnit);
  const displayUnit = respectUnit ? rawUnit : converted.unitDisplay;
  const factor = conversionFactor(rawUnit, displayUnit);
  const displayUnitLabel = formatEmissionUnit(displayUnit);

  const unitText = (val: number | string): string => {
    const n = typeof val === "number" ? val : parseFloat(val);
    const converted = n / factor;
    const formatted = formatLargeNumber(converted);
    return displayUnitLabel ? `${formatted} ${displayUnitLabel}` : formatted;
  };

  const axisText = (val: number | string): string => {
    const n = typeof val === "number" ? val : parseFloat(val);
    const converted = n / factor;
    return formatLargeNumber(converted);
  };

  const { palette, theme: ct } = readThemeColors(isDark);

  const base: Record<string, unknown> = {
    backgroundColor: "transparent",
    color: palette.slice(),
    textStyle: { color: ct.color, fontFamily: getChartFontFamily() },
    ...(spec.showTitle !== false
      ? {
          title: {
            text: title,
            left: "left",
            textStyle: { fontSize: 14, fontWeight: 600, color: ct.primary },
          },
        }
      : {}),
    legend: {
      show: spec.showLegend !== false,
      type: "scroll",
      bottom: 0,
      icon: "roundRect",
      itemWidth: 12,
      itemHeight: 8,
      textStyle: { color: ct.secondary },
      formatter: translateNode,
    },
    toolbox: {
      show: true,
      right: 10,
      top: 0,
      feature: {
        saveAsImage: { title: translateNode("Save as PNG"), pixelRatio: 2 },
      },
    },
  };
  return {
    spec,
    chartType,
    data,
    isDark,
    width,
    translateNode,
    displayUnit,
    displayUnitLabel,
    factor,
    unitText,
    axisText,
    palette,
    ct,
    base,
  };
}
export type ChartContext = ReturnType<typeof createChartContext>;
